<template>
  <div class="flex justify-center items-start mb-3 mt-1">
    <div class="relative inline-flex keyboard-frame" :class="{ 'is-ready': frameReady }">
      <div v-if="currentDevice && layout" class="relative">
        <KeyboardFrame :assets-dir="assetsDir" @ready="frameReady = $event" />

        <div
          ref="keyboardRef"
          class="absolute left-0 top-0 w-full h-full flex items-center justify-center"
          :style="{
            paddingTop: (layout.top ?? 0) + 'px',
            paddingLeft: (layout.left ?? 0) + 'px',
            paddingBottom: (layout.bottom ?? 0) + 'px',
            paddingRight: (layout.right ?? 0) + 'px',
          }"
          @contextmenu="handleKeyboardContextmenu"
        >
          <Keyboard
            class="shrink-0"
            :keyboard-config="keyboardConfig!"
            :gap="KEYBOARD_GAP"
            :native-event-names="keyboardEventNames"
            :key-class="getKeyClass"
            :key-style="getKeyStyle"
            @key-event="handleKeyEvent"
          >
            <template #widget>
              <component :is="widgetMap[model]" v-if="model" :styles="indicatorStyles" />
            </template>
            <template #key-label="{ config }">
              <template v-if="ICON_KEYWORDS.includes(config.keywords)">
                <div class="flex items-center justify-center w-7 h-7">
                  <Icon :icon="ICON_KEYWORDS_MAP[config.keywords]" class="size-full" />
                </div>
              </template>
              <!-- V700diy-87 三个灯效功能键：展示对应功能 icon -->
              <template v-else-if="isV700Diy87SpecialKey(model, config.keywords)">
                <div class="flex items-center justify-center w-7 h-7">
                  <Icon :icon="V700DIY87_SPECIAL_ICON_MAP[config.keywords]" class="size-full" />
                </div>
              </template>
              <div
                v-else
                class="relative w-full h-full flex items-center justify-center select-none"
              >
                <span class="whitespace-pre-wrap leading-4">{{
                  getLocalizedField(config.alias ?? config.name)
                }}</span>
              </div>
            </template>
          </Keyboard>
        </div>
      </div>

      <div v-if="canEditSelection" class="absolute top-0 left-full flex flex-col gap-3 pl-4 pt-4">
        <Button color="glass" class="glass-frosted" @click="handleSelectAll">
          {{ $t('lighting.select.selectAll') }}
        </Button>
        <Button color="glass" @click="handleInvertSelection">
          {{ $t('lighting.select.invertSelection') }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { CSSProperties, HTMLAttributes } from 'vue'
  import { computed, onActivated, onBeforeUnmount, onDeactivated, ref, watch } from 'vue'
  import { storeToRefs } from 'pinia'
  import {
    hasLightingEditableField,
    LIGHTING_EDITABLE_FIELD,
    rgbToCss,
    type LightingEffectDefinition,
    type RGB,
    type RGBGroup,
    type keyMapLighting,
  } from '@rapoo/device-sdk'
  import type {
    KeyboardConfig,
    KeyboardKeyEventPayload,
    KeyEventName,
    KeyConfig,
  } from '@rapoo/element'
  import widgetMap from '@/components/keyboardWidget'
  import KeyboardFrame from '@/components/KeyboardFrame.vue'
  import { Icon, Keyboard, Button, toast } from '@rapoo/element'
  import { useDeviceStore, useLocaleStore } from '@/stores'
  import {
    KEYCODE_KEYWORDS_MAP,
    getLocalizedField,
    isV700Diy87SpecialKey,
    V700DIY87_SPECIAL_ICON_MAP,
  } from '../../../keys/utils/keyMapSettings'
  import { useGlobalContextmenu } from '@/composables/useGlobalContextmenu'
  import { resolvePresetEffectKeyMap } from '../../composables/usePresetEffect'

  // 键盘组件需要监听的原生事件类型
  const keyboardEventNames: KeyEventName[] = ['mousedown', 'mouseenter', 'click']

  // 初始按键内容为图标的键
  const ICON_KEYWORDS = ['RT', 'LEDMode_Forward', 'SideLED_Mode', 'LED_circulate']

  // 图标按键标签映射
  const ICON_KEYWORDS_MAP: Record<string, string> = {
    RT: 'Hardware-Fill-rt',
    LEDMode_Forward: 'LightEffect-Wireframe-BacklightSwitch',
    SideLED_Mode: 'LightEffect-Wireframe-SideLightSwitch',
    LED_circulate: 'LightEffect-Wireframe-DecorLight',
  }

  // 键帽之间的间隔
  const KEYBOARD_GAP = 5
  /** 音乐律动空闲键底色（与测试页 KeyboardPreview 未点亮键一致） */
  const AUDIO_RHYTHM_IDLE_BACKGROUND = '#ffffff4d'
  // 右键清空选择提示 toast 唯一标识
  const LIGHT_TOAST_ID = 'keyboard'

  const props = defineProps<{
    rgb: { r: number; g: number; b: number }
    rgbGroup: RGBGroup
    isColorful: boolean
    gradientDirection: number // 1 无渐变 3 水平 2 垂直
    currentEffectInfo?: Pick<
      LightingEffectDefinition,
      'i18nKey' | 'isCustom' | 'editableFields'
    > | null
    keyMapRgb: keyMapLighting
    selectedKeys: { index: number }[]
    canEditSelection: boolean
    /** RGB 指示灯部件发光样式；不传则部件默认不发光 */
    indicatorStyles?: CSSProperties
    /** 音乐律动：background 填色，空闲键为深色 */
    fillKeys?: boolean
  }>()

  const emit = defineEmits<{
    (e: 'selection-change', keys: { index: number }[]): void
  }>()

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const { t } = useLocaleStore()

  // 设备布局配置（键盘矩阵、边距、单位尺寸）
  const layout = computed(() => currentDevice.value?.manifest.layout)
  const model = computed(() => currentDevice.value?.manifest.model)
  const assetsDir = computed(() => currentDevice.value?.manifest?.ui?.assetsDir || '')
  const frameReady = ref(false)
  const isPanelActive = ref(true)

  // 适配 Keyboard 的布局配置（把 w/h/x/y 转成 rect）
  const keyboardConfig = computed<KeyboardConfig | null>(() => {
    if (!layout.value) return null

    return {
      ...layout.value,
      keys: layout.value.keys.map((row) =>
        row.map((key) => {
          const { w, h, x, y, keywords, ...rest } = key
          const config: KeyConfig = KEYCODE_KEYWORDS_MAP.get(keywords)!

          return {
            ...rest,
            ...config,
            rect: {
              w,
              h,
              x,
              y,
            },
          }
        }),
      ),
    }
  })

  // 键帽基准单位尺寸（像素）
  const keyboardUnit = computed(() => keyboardConfig.value?.unit ?? 40)
  // 键帽步距 = 单位尺寸 + 间隔
  const keyboardPitch = computed(() => keyboardUnit.value + KEYBOARD_GAP)

  // 扁平化键位数据，每个键以 index 作为唯一标识，并补充像素布局
  const keyEntries = computed(() => {
    if (!layout.value) return [] as { key: KeyConfig; index: number; indexes: number[] }[]

    // 展平后的键位条目列表
    const normalized: { key: KeyConfig; index: number; indexes: number[] }[] = []

    layout.value.keys.forEach((rowKeys, rowIndex) => {
      let cursorX = 0

      rowKeys.forEach((rowKey) => {
        const config: KeyConfig = KEYCODE_KEYWORDS_MAP.get(rowKey.keywords)!

        // 键位尺寸/偏移，缺省按 1u 处理
        const { w = 1, h = 1, x = 0, y = 0 } = rowKey
        cursorX += x

        // 仅用于像素定位的纵向网格位置（非矩阵坐标）
        const cursorY = rowIndex + y

        normalized.push({
          // 键位索引（与设备端灯效表一致，来源于布局的 key 字段）
          index: rowKey.key,
          // 主索引 + 长键额外 LED 灯珠索引（空格等），写入/读取时一并参与
          indexes: [rowKey.key, ...(rowKey.ledIndexes ?? [])],
          key: {
            ...rowKey,
            ...config,
            rect: {
              x: cursorX,
              y: cursorY,
              w,
              h,
            },
            layout: {
              left: Math.round(cursorX * keyboardPitch.value),
              top: Math.round(cursorY * keyboardPitch.value),
              width: Math.round(w * keyboardUnit.value + (w - 1) * KEYBOARD_GAP),
              height: Math.round(h * keyboardUnit.value + (h - 1) * KEYBOARD_GAP),
            },
          },
        })

        cursorX += w
      })
    })

    return normalized
  })

  // 供预设灯效算法使用的键位输入数据
  const presetSourceKeys = computed(() => {
    return keyEntries.value.map((item) => ({
      index: item.index,
      keyCode: item.key.keywords,
      label: getLocalizedField(item.key.alias ?? item.key.name),
      code: item.key.code,
      left: item.key.layout?.left,
      top: item.key.layout?.top,
      width: item.key.layout?.width,
      height: item.key.layout?.height,
    }))
  })

  // 预设灯效生成出的键位颜色表（自定义模式下不使用）
  const presetPreviewEffect = computed<string | null>(() => {
    const effectInfo = props.currentEffectInfo
    if (!effectInfo?.i18nKey || effectInfo.isCustom) return null

    if (hasLightingEditableField(effectInfo, LIGHTING_EDITABLE_FIELD.Gradient)) {
      return effectInfo.i18nKey
    }

    // 炫彩预览只看 isColorful，不再靠 i18nKey 去 PRESET_ALIASES 查 random
    if (
      hasLightingEditableField(effectInfo, LIGHTING_EDITABLE_FIELD.ColorCycle) &&
      props.isColorful
    ) {
      return 'random'
    }

    // 可改单色且非炫彩 → 用面板 rgb
    if (hasLightingEditableField(effectInfo, LIGHTING_EDITABLE_FIELD.Color)) {
      return null
    }

    // 蓝天白云 / MOBA / FPS 等无 Color、无炫彩开关的静态构图预设
    return effectInfo.i18nKey
  })

  const presetEffectKeyMap = computed<keyMapLighting | null>(() => {
    if (!presetPreviewEffect.value) return null

    return resolvePresetEffectKeyMap({
      effect: presetPreviewEffect.value,
      keys: presetSourceKeys.value,
      gradientColors: props.rgbGroup,
    })
  })

  // 自定义双色渐变模式下，复用预设算法生成键位渐变预览
  const customGradientEffect = computed<'row_gradient' | 'col_gradient' | null>(() => {
    if (!props.currentEffectInfo?.isCustom) return null
    if (![2, 3].includes(props.gradientDirection)) return null
    return props.gradientDirection === 3 ? 'row_gradient' : 'col_gradient'
  })

  const customGradientKeyMap = computed<keyMapLighting | null>(() => {
    if (!customGradientEffect.value) return null
    return resolvePresetEffectKeyMap({
      effect: customGradientEffect.value,
      keys: presetSourceKeys.value,
      gradientColors: props.rgbGroup,
    })
  })

  // keyCode -> 键位信息（主索引 + 全部 LED 索引）映射
  const keyInfoMap = computed(() => {
    return new Map(
      keyEntries.value.map((item) => [
        item.key.keywords,
        { index: item.index, indexes: item.indexes },
      ]),
    )
  })

  // 当前模式下允许被框选的键位列表
  const selectableKeys = computed(() => {
    return keyEntries.value.filter((item) => isKeySelectable(item.key)).map((item) => item.key)
  })

  // 当前已选中的键位列表
  const currentSelection = ref<KeyConfig[]>([])
  // 是否处于拖拽选择中
  const isDraggingSelection = ref(false)
  // 拖拽模式：选中或取消选中
  // const dragMode = ref<'select' | 'deselect' | null>(null)
  // 拖拽起始键位
  const dragStartKey = ref<KeyConfig | null>(null)
  // 本次拖拽已处理过的键，避免重复处理
  const dragVisitedKeys = ref(new Set<string>())
  // 拖拽过程中是否发生过跨键移动
  const dragMoved = ref(false)
  // 需要抑制click的keyCode
  const suppressedClickKeyCode = ref<string | null>(null)
  // 拖拽过程中最后一个经过的 keyCode
  const dragLastKeyCode = ref<string | null>(null)

  const shouldListenGlobalContextmenu = computed(
    () => isPanelActive.value && Boolean(currentSelection?.value.length),
  )

  const cancelSelectedKeyByGlobalContextmenu = () => {
    if (!currentSelection?.value.length) return

    currentSelection.value = []
    emit('selection-change', [])
    toast.dismiss(LIGHT_TOAST_ID)
  }

  // 全局监听右键取消选中
  useGlobalContextmenu({
    enabled: shouldListenGlobalContextmenu,
    onContextmenu: cancelSelectedKeyByGlobalContextmenu,
  })

  /**
   * 根据按键编码获取键位信息（索引）
   */
  const getKeyInfo = (key: KeyConfig) => {
    return keyInfoMap.value.get(key.keywords)
  }

  /**
   * 判断按键当前是否允许被选中
   */
  const isKeySelectable = (key: KeyConfig) => {
    if (!props.canEditSelection) return false

    const info = getKeyInfo(key)
    if (!info) return false

    return true
  }

  /**
   * 同步对外抛出的选中键位（全部 LED 索引，长键如空格会展开为多个索引）
   */
  const emitSelectionChange = () => {
    emit(
      'selection-change',
      currentSelection.value
        .map((key) => getKeyInfo(key))
        .filter((item): item is { index: number; indexes: number[] } => Boolean(item))
        .flatMap((item) => item.indexes.map((index) => ({ index }))),
    )
  }

  /**
   * 判断当前按键是否已被选中
   */
  const hasSelected = (keyCode: string) => {
    return currentSelection.value.some((item) => item.keywords === keyCode)
  }

  /**
   * 设置单个按键的选中状态
   */
  const setKeySelected = (key: KeyConfig, selected: boolean) => {
    if (!isKeySelectable(key)) return

    if (selected) {
      if (hasSelected(key.keywords)) return
      currentSelection.value = [...currentSelection.value, key]
      emitSelectionChange()
      return
    }

    currentSelection.value = currentSelection.value.filter((item) => item.keywords !== key.keywords)
    emitSelectionChange()
  }

  /**
   * 切换单个按键的选中状态
   */
  const toggleKeySelected = (key: KeyConfig) => {
    setKeySelected(key, !hasSelected(key.keywords))
  }

  /**
   * 重置拖拽选择过程中的临时状态
   */
  const resetDragState = () => {
    isDraggingSelection.value = false
    dragStartKey.value = null
    dragVisitedKeys.value = new Set()
    dragMoved.value = false
    dragLastKeyCode.value = null
    window.removeEventListener('mouseup', handleGlobalMouseUp)
  }

  /**
   * 在拖拽过程中对经过的按键应用选中/取消选中
   */
  const applyDragSelection = (key: KeyConfig) => {
    if (dragVisitedKeys.value.has(key.keywords)) return

    dragVisitedKeys.value.add(key.keywords)
    dragLastKeyCode.value = key.keywords
    toggleKeySelected(key)
  }

  /**
   * 鼠标抬起时结束拖拽选择，并处理 click 抑制状态
   */
  const handleGlobalMouseUp = () => {
    if (!isDraggingSelection.value) return
    suppressedClickKeyCode.value = dragMoved.value ? dragLastKeyCode.value : null
    resetDragState()
  }

  const startDragSelection = (key: KeyConfig) => {
    isDraggingSelection.value = true
    dragStartKey.value = key
    dragVisitedKeys.value = new Set()
    dragMoved.value = false
    dragLastKeyCode.value = key.keywords
    suppressedClickKeyCode.value = null

    window.addEventListener('mouseup', handleGlobalMouseUp)
  }

  /**
   * 显示“右键可取消选择”的常驻提示
   */
  const showSelectionToast = () => {
    toast(t('toast.deselectKey'), {
      id: LIGHT_TOAST_ID,
      duration: Infinity,
      class: 'text-state-success',
    })
  }

  /**
   * 关闭选择提示
   */
  const dismissSelectionToast = () => {
    toast.dismiss(LIGHT_TOAST_ID)
  }

  /**
   * 清空当前所有已选键
   */
  const clearAllSelectedKeys = () => {
    if (!currentSelection.value.length) return

    currentSelection.value = []
    emitSelectionChange()
    resetDragState()
    suppressedClickKeyCode.value = null
  }

  /**
   * 处理键盘区域右键：清空选择并关闭提示
   */
  const handleKeyboardContextmenu = (e: MouseEvent) => {
    if (!currentSelection.value.length) return
    e.preventDefault()
    clearAllSelectedKeys()
    dismissSelectionToast()
  }

  /**
   * 选中当前键盘上的全部可选按键
   */
  const handleSelectAll = () => {
    if (!props.canEditSelection) return
    currentSelection.value = [...selectableKeys.value]
    emitSelectionChange()
  }

  /**
   * 反转当前键盘上的按键选中状态
   */
  const handleInvertSelection = () => {
    if (!props.canEditSelection) return

    const selectedKeyCodes = new Set(currentSelection.value.map((item) => item.keywords))
    currentSelection.value = selectableKeys.value.filter(
      (key) => !selectedKeyCodes.has(key.keywords),
    )
    emitSelectionChange()
  }

  /**
   * 处理键盘组件抛出的按键交互事件
   */
  const handleKeyEvent = (ctx: KeyboardKeyEventPayload) => {
    const { eventName, data, nativeEvent } = ctx

    if (!props.canEditSelection) return
    if (!isKeySelectable(data)) return

    if (eventName === 'mousedown') {
      nativeEvent.preventDefault()
      startDragSelection(data)
      return
    }

    if (eventName === 'mouseenter') {
      if (!isDraggingSelection.value) return
      if (dragStartKey.value?.keywords === data.keywords) return

      if (!dragMoved.value && dragStartKey.value) {
        applyDragSelection(dragStartKey.value)
      }

      dragMoved.value = true
      applyDragSelection(data)
      return
    }

    if (eventName === 'click') {
      if (suppressedClickKeyCode.value === data.keywords) {
        suppressedClickKeyCode.value = null
        return
      }

      toggleKeySelected(data)
    }
  }

  /**
   * 组件卸载前清除toast，防止残留提示   */
  onBeforeUnmount(() => {
    dismissSelectionToast()
  })

  onDeactivated(() => {
    isPanelActive.value = false
    resetDragState()
    dismissSelectionToast()
  })

  onActivated(() => {
    isPanelActive.value = true
    if (currentSelection.value.length) showSelectionToast()
  })

  /**
   * 当前键位色表按 LED 索引建表，律动 60fps 时避免每键线性查找。
   */
  const keyMapByIndex = computed(() => {
    const map = new Map<number, RGB>()
    for (const item of props.keyMapRgb) {
      if (item.rgb) map.set(item.index, item.rgb)
    }
    return map
  })

  const lookupKeyMapRgb = (key: KeyConfig, byIndex: Map<number, RGB>): RGB | null => {
    const info = getKeyInfo(key)
    if (!info) return null
    const main = byIndex.get(info.index)
    if (main) return main
    for (const index of info.indexes) {
      const rgb = byIndex.get(index)
      if (rgb) return rgb
    }
    return null
  }

  /**
   * 获取当前按键在键位颜色表中的 RGB。律动预览优先主灯索引，与测试页 KeyboardPreview 一致。
   */
  const getKeyMapRgb = (key: KeyConfig, keyMap: keyMapLighting = props.keyMapRgb): RGB | null => {
    if (keyMap === props.keyMapRgb) return lookupKeyMapRgb(key, keyMapByIndex.value)

    const byIndex = new Map<number, RGB>()
    for (const item of keyMap) {
      if (item.rgb) byIndex.set(item.index, item.rgb)
    }
    return lookupKeyMapRgb(key, byIndex)
  }

  /**
   * 获取当前按键在键位颜色表中的颜色。色值原样输出，不改 alpha。
   */
  const getKeyMapColor = (key: KeyConfig, keyMap: keyMapLighting = props.keyMapRgb) => {
    const rgb = getKeyMapRgb(key, keyMap)
    if (!rgb) return null
    return rgbToCss(rgb)
  }

  /**
   * 返回按键的类名，主要用于处理选中态边框样式
   */
  const getKeyClass = (key: KeyConfig): HTMLAttributes['class'] => {
    if (props.fillKeys) {
      return 'text-text-glass-default pointer-events-none transition-none!'
    }

    const isActive = hasSelected(key.keywords)
    const hoverClass =
      isKeySelectable(key) && 'hover:text-text-glass-hover hover:border-blue-dark-hover'

    return `bg-key transition-all text-black-white ${isActive && 'border-brand-blue1 bg-white text-black hover:text-black!'} ${hoverClass} disabled:text-text-disabled`
  }

  /**
   * 返回按键的行内样式，控制普通模式和炫彩模式下的发光效果
   */
  const getKeyStyle = (key: KeyConfig): CSSProperties => {
    if (props.fillKeys) {
      const rgb = getKeyMapRgb(key, props.keyMapRgb)
      const fill = rgb ? rgbToCss(rgb) : AUDIO_RHYTHM_IDLE_BACKGROUND
      return {
        backgroundColor: fill,
        boxShadow: rgb ? `0 0 8px ${rgbToCss(rgb, 0.5)}` : 'none',
      }
    }

    // 预设静态模式（非炫彩、无渐变）下启用 SDK 读取到的静态键位色
    const isPresetStaticKeyMapMode = !props.isColorful && props.gradientDirection === 1
    // 自定义静态模式（可编辑）下未读到键位色时的兜底判断
    const isCustomMode = Boolean(props.currentEffectInfo?.isCustom)
    const isCustomEditableMode = Boolean(isCustomMode && props.canEditSelection)
    // 自定义模式键位色
    const customKeyMapColor = props.canEditSelection ? getKeyMapColor(key, props.keyMapRgb) : null
    // 自定义双色渐变预览键位色
    const customGradientKeyMapColor =
      !props.canEditSelection && customGradientKeyMap.value
        ? getKeyMapColor(key, customGradientKeyMap.value)
        : null
    // 预设效果算法计算出的键位
    const presetKeyMapColor =
      !props.canEditSelection && presetEffectKeyMap.value
        ? getKeyMapColor(key, presetEffectKeyMap.value)
        : null
    // 预设静态模式的兜底：使用 SDK 返回的静态键位色
    const fallbackPresetSdkColor =
      !props.canEditSelection && isPresetStaticKeyMapMode
        ? getKeyMapColor(key, props.keyMapRgb)
        : null
    // RT 指示灯固定白色，不随灯效/渐变/键位色变化
    if (key.keywords === 'RT') {
      return {
        boxShadow: '0 0 5px rgb(255, 255, 255)',
        // backdropFilter: 'blur(4px)',
      }
    }

    // 默认颜色：自定义模式固定白色，其他模式用当前面板颜色
    let color = isCustomEditableMode
      ? 'rgb(255, 255, 255)'
      : `rgb(${props.rgb.r}, ${props.rgb.g}, ${props.rgb.b})`

    if (customKeyMapColor) {
      color = customKeyMapColor
    } else if (customGradientKeyMapColor) {
      color = customGradientKeyMapColor
    } else if (presetKeyMapColor) {
      color = presetKeyMapColor
    } else if (fallbackPresetSdkColor) {
      color = fallbackPresetSdkColor
    }

    return {
      boxShadow: `0 0 5px ${color}`,
      // backdropFilter: 'blur(4px)',
    }
  }

  /**
   * 监听外部选中键位并回填内部选中键位
   */
  watch(
    () => props.selectedKeys,
    (selection) => {
      const nextSelectedKeys = selection
        .map((item) => keyEntries.value.find((entry) => entry.indexes.includes(item.index))?.key)
        .filter((item): item is KeyConfig => Boolean(item))

      currentSelection.value = nextSelectedKeys
    },
    { deep: true, immediate: true },
  )

  /**
   * 监听选中数量，自动控制提示显示   */
  watch(
    () => currentSelection.value.length,
    (count) => {
      if (isPanelActive.value && count > 0) {
        showSelectionToast()
        return
      }
      dismissSelectionToast()
    },
    { immediate: true },
  )
</script>

<style scoped>
  .keyboard-frame {
    opacity: 0;
    visibility: hidden;
    transition: opacity 200ms ease;
  }

  .keyboard-frame.is-ready {
    opacity: 1;
    visibility: visible;
  }

  @media (prefers-reduced-motion: reduce) {
    .keyboard-frame {
      transition: none;
    }
  }
</style>
